// Helper function to convert hex to HSL
function hexToHSL(hex: string): [number, number, number] {
  // Remove # if present
  hex = hex.replace(/^#/, '')

  // Parse hex values
  const r = parseInt(hex.slice(0, 2), 16) / 255
  const g = parseInt(hex.slice(2, 4), 16) / 255
  const b = parseInt(hex.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const diff = max - min

  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (diff !== 0) {
    s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min)

    switch (max) {
      case r:
        h = ((g - b) / diff + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / diff + 2) / 6
        break
      case b:
        h = ((r - g) / diff + 4) / 6
        break
    }
  }

  return [h * 360, s * 100, l * 100]
}

// Helper function to convert HSL to hex
function hslToHex(h: number, s: number, l: number): string {
  s /= 100
  l /= 100

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2

  let r = 0,
    g = 0,
    b = 0

  if (h >= 0 && h < 60) {
    r = c
    g = x
    b = 0
  } else if (h >= 60 && h < 120) {
    r = x
    g = c
    b = 0
  } else if (h >= 120 && h < 180) {
    r = 0
    g = c
    b = x
  } else if (h >= 180 && h < 240) {
    r = 0
    g = x
    b = c
  } else if (h >= 240 && h < 300) {
    r = x
    g = 0
    b = c
  } else if (h >= 300 && h < 360) {
    r = c
    g = 0
    b = x
  }

  const toHex = (n: number) => {
    const hex = Math.round((n + m) * 255).toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

// Helper function to adjust hue
function adjustHue(hexColor: string, hueShift: number): string {
  const [h, s, l] = hexToHSL(hexColor)
  const newHue = (h + hueShift + 360) % 360
  return hslToHex(newHue, s, l)
}

/**
 * Generate a color for each well using a 10-color palette with cycle-based hue variations.
 * First 10 wells get base colors, subsequent wells get hue-shifted versions.
 *
 * @param wellIndex - The index of the well (0-based)
 * @returns A hex color string
 */
export function generateWellColor(wellIndex: number): string {
  const colors = [
    '#3B82F6', // blue
    '#EF4444', // red
    '#10B981', // emerald
    '#F59E0B', // amber
    '#8B5CF6', // violet
    '#06B6D4', // cyan
    '#F97316', // orange
    '#84CC16', // lime
    '#EC4899', // pink
    '#6B7280', // gray
  ]

  const colorIndex = wellIndex % colors.length
  const cycle = Math.floor(wellIndex / colors.length)
  const baseColor = colors[colorIndex]

  // For the first cycle (wells 0-9), return base color unchanged
  if (cycle === 0) {
    return baseColor
  }

  // For subsequent cycles, shift the hue
  const hueShifts = [0, 20, -20, 35, -35, 50, -50, 65, -65, 80]
  const hueShift = hueShifts[cycle % hueShifts.length]

  // Convert hex to HSL, adjust hue, convert back
  return adjustHue(baseColor, hueShift)
}
